import { useTranslation } from 'react-i18next';
import {
  FormField,
  TextInput,
  PhoneInput,
  detectDefaultPhoneCountry,
  isValidNationalPhone,
  parseStoredPhone,
  type CountryCode,
} from '@vipso/shared';

export interface VendorFormValues {
  name: string;
  phone: string;
  phoneCountry: CountryCode;
  email: string;
}

export function createEmptyVendorForm(): VendorFormValues {
  return {
    name: '',
    phone: '',
    phoneCountry: detectDefaultPhoneCountry(),
    email: '',
  };
}

export const EMPTY_VENDOR_FORM: VendorFormValues = createEmptyVendorForm();

export function vendorFormValues(vendor: {
  name: string;
  phone?: string | null;
  email?: string | null;
}): VendorFormValues {
  const parsed = parseStoredPhone(vendor.phone);
  return {
    name: vendor.name,
    phone: parsed.phone,
    phoneCountry: parsed.country,
    email: vendor.email || '',
  };
}

export function isVendorPhoneValid(values: VendorFormValues): boolean {
  if (!values.phone.trim()) return true;
  return isValidNationalPhone(values.phone, values.phoneCountry);
}

export function VendorForm({
  value,
  onChange,
  phoneError,
}: {
  value: VendorFormValues;
  onChange: (next: VendorFormValues) => void;
  phoneError?: string | null;
}) {
  const { t } = useTranslation();
  const set = <K extends keyof VendorFormValues>(
    key: K,
    next: VendorFormValues[K],
  ) => onChange({ ...value, [key]: next });

  return (
    <>
      <FormField label={t('common.name')} required>
        <TextInput
          value={value.name}
          onChange={(e) => set('name', e.currentTarget.value)}
        />
      </FormField>
      <FormField label={t('common.phone')}>
        <PhoneInput
          value={value.phone}
          country={value.phoneCountry || detectDefaultPhoneCountry()}
          error={phoneError ?? undefined}
          placeholder={t('auth.phonePlaceholder')}
          countrySearchPlaceholder={t('phone.searchCountry')}
          onChange={({ phone, country }) =>
            onChange({ ...value, phone, phoneCountry: country })
          }
        />
      </FormField>
      <FormField label={t('common.email')}>
        <TextInput
          value={value.email}
          onChange={(e) => set('email', e.currentTarget.value)}
        />
      </FormField>
    </>
  );
}
